export interface CanonicalConversionActionInput {
  actionUrl?: string;
  actionRef?: string;
  actionPayload?: Record<string, unknown> | string;
  openInNewTab?: boolean | "yes" | "no";
}

export type ResolvedConversionAction =
  | {
      kind: "navigate";
      url: string;
      openInNewTab: boolean;
      actionRef: null;
      payload: null;
      fallbackUrl: null;
      analyticsTouchpoint: "declared_navigation";
      conversionSemantics: "navigation_only";
    }
  | {
      kind: "platform";
      url: null;
      openInNewTab: false;
      actionRef: string;
      payload: Record<string, unknown>;
      fallbackUrl: string | null;
      analyticsTouchpoint: "declared_platform_action";
      conversionSemantics: "platform_action_request_not_measured";
    };

export interface DeclaredConversionTouchpoint {
  measurement: "declared";
  kind: "navigation" | "platform_action";
  actionRef: string | null;
  url: string | null;
  analyticsTouchpoint: ResolvedConversionAction["analyticsTouchpoint"];
  conversionSemantics: ResolvedConversionAction["conversionSemantics"];
  meaning: string;
}

type ConversionActionProps = Record<string, unknown> & CanonicalConversionActionInput & {
  ctaUrl?: string;
};

function asString(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}

function toBoolean(value: unknown, fallback = false): boolean {
  if (typeof value === "boolean") return value;
  if (value === "yes") return true;
  if (value === "no") return false;
  return fallback;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function parseActionPayload(value: unknown): Record<string, unknown> {
  if (isRecord(value)) {
    return value;
  }

  const raw = asString(value);
  if (!raw) {
    return {};
  }

  try {
    const parsed = JSON.parse(raw) as unknown;
    return isRecord(parsed) ? parsed : {};
  } catch {
    return {};
  }
}

export function resolveConversionAction(
  props: ConversionActionProps,
  defaultUrl: string,
): ResolvedConversionAction {
  const actionRef = asString(props.actionRef);
  const actionUrl = asString(props.actionUrl) || asString(props.ctaUrl);

  if (actionRef) {
    return {
      kind: "platform",
      url: null,
      openInNewTab: false,
      actionRef,
      payload: parseActionPayload(props.actionPayload),
      fallbackUrl: actionUrl || null,
      analyticsTouchpoint: "declared_platform_action",
      conversionSemantics: "platform_action_request_not_measured",
    };
  }

  return {
    kind: "navigate",
    url: actionUrl || defaultUrl,
    openInNewTab: toBoolean(props.openInNewTab, false),
    actionRef: null,
    payload: null,
    fallbackUrl: null,
    analyticsTouchpoint: "declared_navigation",
    conversionSemantics: "navigation_only",
  };
}

export function normalizeConversionActionProps(
  props: Record<string, unknown>,
  defaultUrl: string,
): Record<string, unknown> {
  const action = resolveConversionAction(props as ConversionActionProps, defaultUrl);

  if (action.kind === "platform") {
    return {
      ...props,
      actionUrl: action.fallbackUrl ?? "",
      actionRef: action.actionRef,
      actionPayload: action.payload,
      openInNewTab: false,
    };
  }

  return {
    ...props,
    actionUrl: action.url,
    actionRef: "",
    actionPayload: {},
    openInNewTab: action.openInNewTab,
  };
}

export function describeDeclaredConversionTouchpoint(
  props: Record<string, unknown>,
  defaultUrl: string,
): DeclaredConversionTouchpoint {
  const action = resolveConversionAction(props as ConversionActionProps, defaultUrl);

  if (action.kind === "platform") {
    return {
      measurement: "declared",
      kind: "platform_action",
      actionRef: action.actionRef,
      url: action.fallbackUrl,
      analyticsTouchpoint: action.analyticsTouchpoint,
      conversionSemantics: action.conversionSemantics,
      meaning:
        "This block declares a platform-native action request. Current builder analytics do not confirm clicks, execution, or completed conversions for it yet.",
    };
  }

  return {
    measurement: "declared",
    kind: "navigation",
    actionRef: null,
    url: action.url,
    analyticsTouchpoint: action.analyticsTouchpoint,
    conversionSemantics: action.conversionSemantics,
    meaning:
      "This block declares a navigation CTA. Current builder analytics do not count link clicks or destination outcomes as measured conversions.",
  };
}
